// src/components/MsoBillComparison.tsx
//
// ★★ Q-54 — ONE MSO BILL, AS THE FLAT-RATE PATH CHARGED IT AND AS THE RULE ENGINE WOULD HAVE CHARGED IT.
//
// Read-only. It prices nothing and changes no bill: it exists so the Product Owner and PSD can SEE what wiring the
// engine into live bills would do, bill by bill, before deciding. When a bill holds no MSO inputs yet, the card says
// so and offers the form to record them (the inputs a live bill does not carry, which is what Q-54 asks who supplies).
//
// ⚠ Every comparison names where its facts came from. A DEMONSTRATION basis is seeded, not a fact about the bill,
// and the card says so in words, never by colour alone.
import { plainProse } from '../lib/plain-words'
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { ApiError, api } from '../lib/api'
import { formatCents } from '../lib/format'
import { BASIS_SOURCE_LABEL, describeBasis, describeChange } from '../lib/mso-comparison'
import { msoRefusalLabel } from '../lib/mso-vocabulary'
import type { MsoBasisInput, MsoShadowComparison } from '../lib/types'
import { ErrorCard, LoadingCard } from './StateCards'
import { CategoryBadge } from './StatusBadge'

type View =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'none' }
  | { readonly kind: 'ready'; readonly comparison: MsoShadowComparison }

export function MsoBillComparison({ reference }: { reference: string }) {
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setView({ kind: 'loading' })
    api
      .msoBillComparison(reference)
      .then((r) => {
        if (live) setView(r === null ? { kind: 'none' } : { kind: 'ready', comparison: r.comparison })
      })
      .catch((e: unknown) => {
        if (live) setView({ kind: 'error', message: e instanceof ApiError ? e.message : 'Could not load the comparison.' })
      })
    return () => {
      live = false
    }
  }, [reference, attempt])

  return (
    <section className="pt-card pt-no-print flex flex-col gap-4" aria-labelledby="mso-cmp-heading">
      <div>
        <h2 id="mso-cmp-heading" className="text-lg mb-1">
          MSO engine comparison
        </h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          What the MSO rule engine would have charged this bill, beside what it was charged. This is a comparison
          only: the bill is priced as before and nothing here changes it.
        </p>
      </div>
      {view.kind === 'loading' && <LoadingCard label="Loading the comparison" rows={2} />}
      {view.kind === 'error' && <ErrorCard title="Could not load the comparison" message={view.message} onRetry={() => setAttempt((n) => n + 1)} />}
      {view.kind === 'none' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm m-0" data-testid="no-basis">
            No MSO inputs are recorded for this bill yet. A live bill carries only an amount; the rules also need who the
            beneficiary is and what kind of care it was. Record them to see the comparison.
          </p>
          <BasisForm reference={reference} onRecorded={(c) => setView(c === null ? { kind: 'none' } : { kind: 'ready', comparison: c })} />
        </div>
      )}
      {view.kind === 'ready' && (
        <div className="flex flex-col gap-4">
          <Comparison comparison={view.comparison} />
          <details>
            <summary className="cursor-pointer text-sm font-semibold">Change the MSO inputs</summary>
            <div className="mt-3">
              <BasisForm
                reference={reference}
                initial={view.comparison}
                onRecorded={(c) => setView(c === null ? { kind: 'none' } : { kind: 'ready', comparison: c })}
              />
            </div>
          </details>
        </div>
      )}
    </section>
  )
}

export function Comparison({ comparison }: { comparison: MsoShadowComparison }) {
  const { flat, engine, basis } = comparison
  const refused = engine.outcome === 'REFUSED'
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm m-0" data-testid="basis-sentence">
        <span className="font-semibold">The inputs:</span> {describeBasis(basis)}. <span>{BASIS_SOURCE_LABEL[basis.source]}</span>
        {basis.recordedByName !== null && basis.source === 'OFFICER_ENTRY' ? <span>, {basis.recordedByName}</span> : null}.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="pt-well p-4">
          <h3 className="text-base font-semibold mt-0 mb-2">Charged today (flat rate)</h3>
          {flat.computed ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm m-0">
              <dt>Government pays</dt>
              <dd className="pt-tabular m-0">{formatCents(flat.governmentCents ?? 0)}</dd>
              <dt>Officer pays</dt>
              <dd className="pt-tabular m-0">{formatCents(flat.officerCents ?? 0)}</dd>
              <dt>Rule</dt>
              <dd className="pt-mono m-0">
                {flat.ruleId} v{flat.ruleVersion}
              </dd>
            </dl>
          ) : (
            <p className="text-sm m-0">This bill has not been computed yet, so there is nothing charged to compare with.</p>
          )}
        </div>
        <div className="pt-well p-4">
          <h3 className="text-base font-semibold mt-0 mb-2">Would be charged (rule engine)</h3>
          {refused ? (
            <p className="text-sm m-0" data-testid="engine-refused">
              <CategoryBadge kind="outcome" value="REFUSED" label="Refused" />
              <span className="sr-only">: </span>
              <CategoryBadge kind="refusal" value={engine.refusalKind ?? 'REFUSED'} label={msoRefusalLabel(engine.refusalKind ?? 'REFUSED')} />{' '}
              {engine.refusalMessage}
            </p>
          ) : (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm m-0">
              <dt>Government pays</dt>
              <dd className="pt-tabular m-0">{formatCents(engine.governmentCents ?? 0)}</dd>
              <dt>Officer pays</dt>
              <dd className="pt-tabular m-0">{formatCents(engine.officerCents ?? 0)}</dd>
              <dt>Rule</dt>
              <dd className="pt-mono m-0">
                {engine.ruleKey} v{engine.ruleVersion}
              </dd>
              <dt>Row</dt>
              <dd className="m-0">{engine.rowId}</dd>
            </dl>
          )}
          {engine.capStatusSource !== null && <p className="text-xs mt-2 mb-0 text-[var(--sgds-body-color-subtle)]">Cap: {engine.capStatusSource}.</p>}
        </div>
      </div>
      <p className="text-sm m-0 font-semibold" data-testid="change-sentence">
        {refused ? 'No comparison: the engine refused this combination.' : describeChange(comparison.governmentChangeCents)}
      </p>
      {comparison.componentAssumption !== null && <p className="text-xs m-0 text-[var(--sgds-body-color-subtle)]">{comparison.componentAssumption}</p>}
      <p className="text-sm m-0">
        <Link to="/mso/calculator">Try other inputs in the calculator</Link>
      </p>
    </div>
  )
}

const BENEFICIARY = [
  ['PRINCIPAL', 'Principal (the officer)'],
  ['DEPENDANT', 'Dependant'],
] as const
const CARE = [
  ['OUTPATIENT', 'Outpatient'],
  ['INPATIENT', 'Inpatient'],
] as const
const VISIT = [
  ['', 'Not stated'],
  ['FIRST_CONSULTATION', 'First consultation'],
  ['FOLLOW_UP', 'Follow-up'],
] as const
const PROVIDER = [
  ['', 'Not stated'],
  ['SPECIALIST', 'Specialist'],
  ['POLYCLINIC', 'Polyclinic'],
  ['HOSPITAL', 'Hospital'],
] as const
const INDICATOR = [
  ['NORMAL', 'Normal'],
  ['MEDISAVE', 'Medisave'],
] as const
const CONDITION = [
  ['NONE', 'None'],
  ['DETERMINATION_OF_FITNESS', 'Determination of Fitness'],
  ['SERVICE_INJURY', 'Service Injury'],
] as const

function BasisForm({
  reference,
  initial,
  onRecorded,
}: {
  reference: string
  initial?: MsoShadowComparison
  onRecorded: (comparison: MsoShadowComparison | null) => void
}) {
  const id = useId()
  const b = initial?.basis
  const [beneficiaryType, setBeneficiaryType] = useState<string>(b?.beneficiaryType ?? 'PRINCIPAL')
  const [careSetting, setCareSetting] = useState<string>(b?.careSetting ?? 'OUTPATIENT')
  const [visitType, setVisitType] = useState<string>(b?.visitType ?? '')
  const [hspType, setHspType] = useState<string>(b?.hspType ?? '')
  const [billIndicator, setBillIndicator] = useState<string>(b?.billIndicator ?? 'NORMAL')
  const [condition, setCondition] = useState<string>(b?.specialBillCondition ?? 'NONE')
  const [upgrade, setUpgrade] = useState<boolean>(b?.voluntaryWardUpgrade ?? false)
  const [beneficiaryRef, setBeneficiaryRef] = useState<string>(b?.beneficiaryRef ?? '')
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string }>({ kind: 'idle' })
  const inpatient = careSetting === 'INPATIENT'

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (state.kind === 'saving') return
    setState({ kind: 'saving' })
    const body: MsoBasisInput = {
      beneficiaryType,
      careSetting,
      billIndicator,
      specialBillCondition: condition,
      ...(visitType !== '' && { visitType }),
      ...(hspType !== '' && { hspType }),
      ...(beneficiaryRef.trim() !== '' && { beneficiaryRef: beneficiaryRef.trim() }),
      ...(inpatient && { voluntaryWardUpgrade: upgrade }),
    }
    try {
      const r = await api.recordMsoBasis(reference, body)
      setState({ kind: 'idle' })
      onRecorded(r.comparison)
    } catch (error) {
      setState({ kind: 'error', message: error instanceof ApiError ? error.message : 'Could not record the inputs.' })
    }
  }

  const select = (name: string, label: string, value: string, set: (v: string) => void, options: readonly (readonly [string, string])[]) => (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={`${id}-${name}`} className="font-semibold">
        {label}
      </label>
      <select id={`${id}-${name}`} className="pt-native-date" value={value} onChange={(e) => set(e.target.value)}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  )

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" aria-label="Record the MSO inputs for this bill">
      <div className="grid gap-4 sm:grid-cols-2">
        {select('who', 'Beneficiary', beneficiaryType, setBeneficiaryType, BENEFICIARY)}
        {select('care', 'Care setting', careSetting, setCareSetting, CARE)}
        {!inpatient && select('visit', 'Visit', visitType, setVisitType, VISIT)}
        {!inpatient && select('provider', 'Healthcare provider type', hspType, setHspType, PROVIDER)}
        {!inpatient && select('indicator', 'Bill indicator', billIndicator, setBillIndicator, INDICATOR)}
        {inpatient && select('condition', 'Special bill condition', condition, setCondition, CONDITION)}
        {inpatient && (
          <div className="flex items-center gap-2 text-sm">
            <input id={`${id}-upgrade`} type="checkbox" checked={upgrade} onChange={(e) => setUpgrade(e.target.checked)} />
            <label htmlFor={`${id}-upgrade`} className="font-semibold">
              Voluntary ward upgrade
            </label>
          </div>
        )}
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${id}-ref`} className="font-semibold">
            Beneficiary reference (optional)
          </label>
          <input id={`${id}-ref`} className="pt-native-date" value={beneficiaryRef} maxLength={40} onChange={(e) => setBeneficiaryRef(e.target.value)} aria-describedby={`${id}-ref-hint`} />
          <span id={`${id}-ref-hint`} className="text-xs text-[var(--sgds-body-color-subtle)]">
            An opaque label, for example DEMO-BEN-0001. It lets the annual cap be read from the ledger. No name or NRIC.
          </span>
        </div>
      </div>
      {state.kind === 'error' && (
        <p role="alert" className="text-sm m-0 text-[var(--sgds-danger-color-default)]">
          {plainProse(state.message)}
        </p>
      )}
      <div>
        <SgdsButton type="submit" variant="primary" aria-busy={state.kind === 'saving'}>
          {state.kind === 'saving' ? 'Recording' : 'Record the inputs and compare'}
        </SgdsButton>
      </div>
    </form>
  )
}
